import React, { useState, useEffect } from 'react';
import {
  BuildingLibraryIcon as Building2,
  FunnelIcon as Filter,
  MagnifyingGlassIcon as Search,
  PencilIcon as Edit3,
  CheckIcon as Check,
  XMarkIcon as X,
  EyeIcon as Eye,
  CalendarIcon as Calendar,
  CurrencyDollarIcon as DollarSign,
  TagIcon as Tag,
  DocumentTextIcon as FileText,
  ChevronDownIcon as ChevronDown,
  ChevronUpIcon as ChevronUp
} from '@heroicons/react/24/outline';
import { api } from '@/services/api';

interface BankAccount {
  id: number;
  name: string;
  bank_name: string;
  linked_account_id: number | null;
  linked_account_name: string | null;
  summary: {
    total_transactions: number;
    pending_classification: number;
    processed: number;
  };
  last_sync: string | null;
}

interface BankStatementEntry {
  id: number;
  date: string;
  description: string;
  amount: number;
  type: string;
  bank_account_name: string;
  bank_account_id: number;
  status: string;
  suggested_category: string | null;
  suggested_category_id: number | null;
  can_edit: boolean;
}

interface Category {
  id: number;
  nome: string;
}

interface Classification {
  description?: string;
  category_id?: number;
  notes?: string;
}

const BankStatementsPage: React.FC = () => {
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [entries, setEntries] = useState<BankStatementEntry[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<'pending' | 'processed' | 'all'>('pending');
  const [typeFilter, setTypeFilter] = useState<'all' | 'credit' | 'debit'>('all');
  const [editingEntry, setEditingEntry] = useState<number | null>(null);
  const [classification, setClassification] = useState<Classification>({});
  const [expandedEntry, setExpandedEntry] = useState<number | null>(null);
  const [selectedEntries, setSelectedEntries] = useState<Set<number>>(new Set());
  const [bulkClassification, setBulkClassification] = useState<Classification>({});


  useEffect(() => {
    loadBankAccounts();
    loadCategories();
  }, []);

  useEffect(() => {
    loadStatementEntries();
  }, [selectedAccount, statusFilter]);

  const loadBankAccounts = async () => {
    try {
      const response = await api.get('/bank-statements/accounts');
      setAccounts(response.data.accounts);
    } catch (error) {
      console.error('Erro ao carregar contas bancárias:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await api.get('/financeiro/categorias');
      setCategories(response.data);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  const loadStatementEntries = async () => {
    try {
      setLoading(true);
      const params: any = {
        status: statusFilter,
        limit: '50'
      };

      if (selectedAccount) {
        params.bank_account_id = selectedAccount.toString();
      }

      const response = await api.get('/bank-statements/entries', { params });
      const data = response.data;

      // Process real data from bank-statements API
      const processedEntries: BankStatementEntry[] = data.entries.map((entry: any) => ({
        id: entry.id,
        date: entry.date,
        description: entry.description,
        amount: parseFloat(entry.amount),
        type: entry.type,
        bank_account_name: entry.bank_account_name,
        bank_account_id: entry.bank_account_id,
        status: entry.status,
        suggested_category: entry.suggested_category,
        suggested_category_id: entry.suggested_category_id,
        can_edit: entry.can_edit
      }));
      setEntries(processedEntries);
    } catch (error) {
      console.error('Erro ao carregar extratos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProcessEntry = async (entryId: number) => {
    try {
      const processData = {
        bank_transaction_id: entryId,
        classification: {
          description: classification.description,
          category_id: classification.category_id,
          notes: classification.notes
        }
      };

      const response = await api.post('/bank-statements/process', processData);
      const result = response.data;
      console.log('Transação processada:', result);

      // Remove entry from list and reset form
      setEntries(prev => prev.filter(e => e.id !== entryId));
      setEditingEntry(null);
      setClassification({});

      // Show success message
      alert('Transação processada com sucesso! Verifique nas abas "Contas a Pagar" ou "Contas a Receber".');
    } catch (error: any) {
      console.error('Erro ao processar transação:', error);
      const errorMessage = error.response?.data?.detail || 'Erro ao processar transação. Verifique a conexão com o servidor.';
      alert(`Erro ao processar transação: ${errorMessage}`);
    }
  };

  const startEditing = (entry: BankStatementEntry) => {
    setEditingEntry(entry.id);
    setClassification({
      description: entry.description,
      category_id: entry.suggested_category_id || undefined,
      notes: ''
    });
  };

  const cancelEditing = () => {
    setEditingEntry(null);
    setClassification({});
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  // Selection management functions
  const handleSelectAll = () => {
    // Aplicar filtro de tipo antes de selecionar
    const filteredEntries = typeFilter === 'all'
      ? entries
      : entries.filter(entry => entry.type === typeFilter);

    if (selectedEntries.size === filteredEntries.length) {
      setSelectedEntries(new Set());
    } else {
      setSelectedEntries(new Set(filteredEntries.map(entry => entry.id)));
    }
  };

  const handleSelectEntry = (entryId: number) => {
    const newSelected = new Set(selectedEntries);
    if (newSelected.has(entryId)) {
      newSelected.delete(entryId);
    } else {
      newSelected.add(entryId);
    }
    setSelectedEntries(newSelected);
  };

  const handleBulkProcess = async () => {
    if (selectedEntries.size === 0) {
      alert('Selecione pelo menos uma transação para processar.');
      return;
    }

    if (!bulkClassification.category_id) {
      alert('Selecione uma categoria para as transações selecionadas.');
      return;
    }

    try {
      const selectedEntriesArray = Array.from(selectedEntries);
      let successCount = 0;
      let errorCount = 0;

      for (const entryId of selectedEntriesArray) {
        try {
          const entry = entries.find(e => e.id === entryId);
          const processData = {
            bank_transaction_id: entryId,
            classification: {
              description: bulkClassification.description || entry?.description,
              category_id: bulkClassification.category_id,
              notes: bulkClassification.notes
            }
          };

          await api.post('/bank-statements/process', processData);
          successCount++;
        } catch (error) {
          console.error(`Erro ao processar transação ${entryId}:`, error);
          errorCount++;
        }
      }

      // Remove processed entries from list and reset selection
      setEntries(prev => prev.filter(e => !selectedEntries.has(e.id)));
      setSelectedEntries(new Set());
      setBulkClassification({});

      alert(`${successCount} transações processadas com sucesso! ${errorCount > 0 ? `${errorCount} falharam.` : ''}`);
    } catch (error: any) {
      console.error('Erro no processamento em lote:', error);
      alert('Erro no processamento em lote. Tente novamente.');
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Extratos Bancários</h1>
          <p className="text-gray-600 mt-1">
            Classifique suas transações bancárias facilmente
          </p>
        </div>
      </div>

      {/* Bank Accounts Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {accounts.map((account) => (
          <div
            key={account.id}
            className={`p-4 border rounded-lg cursor-pointer transition-colors ${
              selectedAccount === account.id
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => setSelectedAccount(selectedAccount === account.id ? null : account.id)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Building2 className="h-8 w-8 text-blue-600" />
                <div>
                  <h3 className="font-semibold text-gray-900">{account.name}</h3>
                  <p className="text-sm text-gray-600">{account.bank_name}</p>
                </div>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
              <div className="text-center p-2 bg-yellow-50 rounded">
                <div className="font-semibold text-yellow-700">{account.summary.pending_classification}</div>
                <div className="text-yellow-600">Pendentes</div>
              </div>
              <div className="text-center p-2 bg-green-50 rounded">
                <div className="font-semibold text-green-700">{account.summary.processed}</div>
                <div className="text-green-600">Processadas</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center space-x-2">
          <Filter className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Filtros:</span>
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as any)}
          className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="pending">Pendentes</option>
          <option value="processed">Processadas</option>
          <option value="all">Todas</option>
        </select>

        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as any)}
          className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
        >
          <option value="all">Todos os tipos</option>
          <option value="credit">💰 Receitas</option>
          <option value="debit">💸 Despesas</option>
        </select>

        <div className="text-sm text-gray-600">
          {selectedAccount
            ? `Filtrado: ${accounts.find(a => a.id === selectedAccount)?.name}`
            : 'Todas as contas'
          }
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedEntries.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-blue-900">
              {selectedEntries.size} transação(ões) selecionada(s)
            </h3>
            <button
              onClick={() => setSelectedEntries(new Set())}
              className="text-blue-600 hover:text-blue-800 text-sm"
            >
              Limpar seleção
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-blue-700 mb-1">
                Descrição (opcional)
              </label>
              <input
                type="text"
                value={bulkClassification.description || ''}
                onChange={(e) => setBulkClassification(prev => ({
                  ...prev,
                  description: e.target.value
                }))}
                className="w-full px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Deixe vazio para manter original"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-blue-700 mb-1">
                Categoria *
              </label>
              <select
                value={bulkClassification.category_id || ''}
                onChange={(e) => setBulkClassification(prev => ({
                  ...prev,
                  category_id: e.target.value ? parseInt(e.target.value) : undefined
                }))}
                className="w-full px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Selecione uma categoria</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.nome}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-blue-700 mb-1">
                Observações (opcional)
              </label>
              <input
                type="text"
                value={bulkClassification.notes || ''}
                onChange={(e) => setBulkClassification(prev => ({
                  ...prev,
                  notes: e.target.value
                }))}
                className="w-full px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Observações..."
              />
            </div>
          </div>

          <div className="mt-3 flex space-x-2">
            <button
              onClick={handleBulkProcess}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
            >
              Processar Selecionadas
            </button>
          </div>
        </div>
      )}

      {/* Statement Entries */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Transações para Classificação
            </h2>
            {entries.length > 0 && (
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={selectedEntries.size === entries.length && entries.length > 0}
                  onChange={handleSelectAll}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label className="text-sm text-gray-700 cursor-pointer" onClick={handleSelectAll}>
                  Selecionar Todas
                </label>
              </div>
            )}
          </div>
        </div>

        <div className="divide-y divide-gray-200">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
              <p className="mt-2 text-gray-600">Carregando transações...</p>
            </div>
          ) : entries.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Nenhuma transação encontrada</p>
            </div>
          ) : (
            (() => {
              // Aplicar filtro de tipo
              const filteredEntries = typeFilter === 'all'
                ? entries
                : entries.filter(entry => entry.type === typeFilter);

              return filteredEntries.length === 0 ? (
                <div className="p-8 text-center">
                  <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Nenhuma transação do tipo selecionado</p>
                </div>
              ) : (
                filteredEntries.map((entry) => (
              <div key={entry.id} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      checked={selectedEntries.has(entry.id)}
                      onChange={() => handleSelectEntry(entry.id)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-1"
                    />
                    <div className="flex-1">
                    <div className="flex items-center space-x-4">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-600">{formatDate(entry.date)}</span>
                          <span className="text-sm text-gray-400">•</span>
                          <Building2 className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-600">{entry.bank_account_name}</span>
                        </div>

                        {editingEntry === entry.id ? (
                          <div className="mt-3 space-y-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Descrição
                              </label>
                              <input
                                type="text"
                                value={classification.description || ''}
                                onChange={(e) => setClassification(prev => ({
                                  ...prev,
                                  description: e.target.value
                                }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Digite a descrição..."
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Categoria
                              </label>
                              <select
                                value={classification.category_id || ''}
                                onChange={(e) => setClassification(prev => ({
                                  ...prev,
                                  category_id: e.target.value ? parseInt(e.target.value) : undefined
                                }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="">Selecione uma categoria</option>
                                {categories.map(cat => (
                                  <option key={cat.id} value={cat.id}>{cat.nome}</option>
                                ))}
                              </select>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Observações
                              </label>
                              <textarea
                                value={classification.notes || ''}
                                onChange={(e) => setClassification(prev => ({
                                  ...prev,
                                  notes: e.target.value
                                }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                rows={2}
                                placeholder="Observações adicionais..."
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="mt-2">
                            <p className="text-gray-900 font-medium">{entry.description}</p>
                            {entry.suggested_category && (
                              <div className="flex items-center space-x-2 mt-1">
                                <Tag className="h-3 w-3 text-blue-500" />
                                <span className="text-xs text-blue-600">
                                  Sugestão: {entry.suggested_category}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="text-right">
                        <div className={`text-lg font-semibold ${
                          entry.amount >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {formatCurrency(entry.amount)}
                        </div>
                        <div className="text-sm text-gray-500">
                          {entry.amount >= 0 ? 'Entrada' : 'Saída'}
                        </div>
                      </div>
                    </div>
                    </div>
                  </div>

                  <div className="ml-4 flex items-center space-x-2">
                    {editingEntry === entry.id ? (
                      <>
                        <button
                          onClick={() => handleProcessEntry(entry.id)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-full transition-colors"
                          title="Confirmar e processar"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <button
                          onClick={cancelEditing}
                          className="p-2 text-gray-600 hover:bg-gray-50 rounded-full transition-colors"
                          title="Cancelar"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => startEditing(entry)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                          title="Editar e classificar"
                        >
                          <Edit3 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setExpandedEntry(
                            expandedEntry === entry.id ? null : entry.id
                          )}
                          className="p-2 text-gray-600 hover:bg-gray-50 rounded-full transition-colors"
                          title="Ver detalhes"
                        >
                          {expandedEntry === entry.id ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {expandedEntry === entry.id && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-gray-700">ID da Transação:</span>
                        <span className="ml-2 text-gray-600">{entry.id}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Tipo:</span>
                        <span className="ml-2 text-gray-600">{entry.type}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Status:</span>
                        <span className="ml-2 text-gray-600">{entry.status}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Pode Editar:</span>
                        <span className="ml-2 text-gray-600">{entry.can_edit ? 'Sim' : 'Não'}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))
              );
            })()
          )}
        </div>
      </div>
    </div>
  );
};

export default BankStatementsPage;